import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { Fragment, useEffect, useState, useRef } from "react"; // 👈 useRef
import { styled } from "@mui/material/styles";
import { fDateTimeDate } from "src/utils/formatTime";
import { DialogAnimate } from "src/components/Animate";
import { useTranslation } from "react-i18next";
import Markdown from "src/components/Markdown";
import MyAutoSnack from "src/components/MyAutoSnack";
import { useSnackbar } from "notistack";

// NUEVO: contenedor con scroll
const ScrollableContent = styled("div")(({ theme }) => ({
  maxHeight: "60vh",
  overflowY: "auto",
  paddingRight: theme.spacing(1),
}));

const MarkdownWrapperStyle = styled("div")(({ theme }) => ({
  "& > p": {
    ...theme.typography.body1,
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const DialogStyle = styled(DialogAnimate)(({ theme }) => ({
  "& .MuiDialog-paper": {
    margin: 0,
    [theme.breakpoints.up("md")]: {
      maxWidth: "calc(90% - 48px)",
      maxHeight: "calc(85% - 48px)",
    },
  },
  ".css-ji9w9w-MuiDialog-paper.MuiDialog-paperFullScreen": {
    borderRadius: 15,
  },
}));

export default function NotificationsPreview({
  open,
  onClose,
  notifications = [],
  markAsRead,
}) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState(notifications);
  const [currentNotification, setCurrentNotification] = useState(items[0]);

  // Referencia al contenedor con scroll para resetear la posición al
  // cambiar de aviso (multi-notificación).
  const scrollRef = useRef(null);

  useEffect(() => {
    if (currentNotification?.isUnRead && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [currentIndex, currentNotification?.id]);


  const handleMarkAsRead = (id) => {
    if (!currentNotification) return;

    if (markAsRead) {
      markAsRead(id);
    }

    // Actualizamos el estado local para reflejar que ya no está sin leer
    setItems((prev) =>
      prev.map((n) =>
        n.id === id
          ? {
              ...n,
              isUnRead: false,
            }
          : n
      )
    );
  };

  const handleNext = () => {
    if (!currentNotification) return;

    // Si hay más notificaciones, avanzamos; si no, cerramos
    if (currentIndex < items.length - 1) {
      setCurrentNotification(items[currentIndex + 1]);
      setCurrentIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentNotification(items[currentIndex - 1]);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  if (!currentNotification) {
    return null;
  }

  return (
    <DialogStyle
      open={open}
      onClose={() => {
        MyAutoSnack(
          "warning",
          "Debe finalizar las notificaciones obligatorias antes de cerrar.",
          enqueueSnackbar,
          closeSnackbar
        );
      }}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={0.5}>
            <Typography variant="h4">{currentNotification.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {fDateTimeDate(currentNotification.creationDate)}
            </Typography>
            <Typography variant="h6">
              {currentNotification.description}
            </Typography>
          </Stack>

          {/* Indicador de posición actual */}
          <Typography variant="subtitle1">
            {currentIndex + 1} / {items.length}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent ref={scrollRef}>
        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <MarkdownWrapperStyle>
              <Markdown children={currentNotification.content} />
            </MarkdownWrapperStyle>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            {t("tucarta.Previous", "Anterior")}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Fragment>
            {/* Aceptar SIEMPRE habilitado. La lógica anterior exigía
                haber scrolleado hasta el final del contenido antes de
                aceptar, pero avisos cortos (que caben en pantalla sin
                scroll) dejaban el botón bloqueado para siempre porque
                el evento `onScroll` nunca disparaba. Preferimos permitir
                aceptar directamente — la enforcement de "leer todo"
                nunca es real (el user puede hacer scroll sin leer) y
                atrapaba a los users en el modal bloqueante. */}
            <Button
              variant="outlined"
              onClick={() => {
                if (currentNotification.isUnRead) {
                  handleMarkAsRead(currentNotification.id);
                }
                handleNext();
              }}
            >
              {currentNotification.isMandatory
                ? "Aceptar"
                : t("tucarta.MarkAsReaded")}
            </Button>
          </Fragment>
        </Stack>
      </DialogActions>
    </DialogStyle>
  );
}
